import type { PaginatedResponse } from '@/api/pagination'
import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/lib/query-keys'

export interface MachineOperator {
  id: number
  first_name: string
  last_name: string
  email: string
  email_optin: boolean
}

export interface AttachableUser {
  id: number
  first_name: string
  last_name: string
  email: string
}

export interface CreateOperatorData {
  first_name: string
  last_name: string
  email: string
  email_optin?: boolean
}

export interface AttachOperatorData {
  user_id: number
  email_optin?: boolean
}

export interface UpdateOperatorData {
  email_optin: boolean
}

export async function fetchMachineOperators(
  machineCode: string,
  params?: { search?: string },
): Promise<MachineOperator[]> {
  const response = await api.get<{ data: MachineOperator[] }>(
    `/machines/${machineCode}/operators`,
    { params },
  )
  return response.data
}

export function machineOperatorsQueryOptions(machineCode: string) {
  return queryOptions({
    queryKey: queryKeys.machines.operators(machineCode),
    queryFn: async () => fetchMachineOperators(machineCode),
  })
}

export async function fetchAttachableUsers(
  machineCode: string,
  params?: { page?: number; search?: string },
): Promise<PaginatedResponse<AttachableUser>> {
  return api.get<PaginatedResponse<AttachableUser>>(
    `/machines/${machineCode}/operators/attachable-users`,
    { params },
  )
}

export function attachableUsersQueryOptions(machineCode: string, search?: string) {
  return queryOptions({
    queryKey: [...queryKeys.machines.attachableUsers(machineCode), { search }],
    queryFn: async () => fetchAttachableUsers(machineCode, { search }),
    enabled: search !== undefined && search.length >= 2,
  })
}

export async function createOperator(
  machineCode: string,
  data: CreateOperatorData,
): Promise<{ message: string; data: MachineOperator }> {
  return api.post<{ message: string; data: MachineOperator }>(
    `/machines/${machineCode}/operators`,
    {
      body: data,
    },
  )
}

export async function attachOperator(
  machineCode: string,
  data: AttachOperatorData,
): Promise<{ message: string; data: MachineOperator }> {
  return api.post<{ message: string; data: MachineOperator }>(
    `/machines/${machineCode}/operators/attach`,
    { body: data },
  )
}

export async function updateOperator(
  machineCode: string,
  userId: number,
  data: UpdateOperatorData,
): Promise<{ message: string; data: MachineOperator }> {
  return api.patch<{ message: string; data: MachineOperator }>(
    `/machines/${machineCode}/operators/${userId}`,
    { body: data },
  )
}

export async function deleteOperator(
  machineCode: string,
  userId: number,
): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/machines/${machineCode}/operators/${userId}`)
}
